// Core React and required hooks
import React, { useState } from 'react';

// MobX bindings to inject global state and observe updates
import { observer, inject } from "mobx-react";

// React Router hook for page navigation
import { useNavigate } from 'react-router-dom';

// App assets
import logo from './../assets/img/logo-xpens-cropped.svg';

// Localization engine for internationalization
import Translator from '../locales/translator';

// API wrapper and auth utilities
import { fetch } from '../utils/api-wrapper';
import AuthUtils from '../utils/auth-utils';

// PWA install prompt button
import PwaInstallButton from '../components/PwaInstallButton';

// Font Awesome icons for eye toggle (show/hide password)
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

// Wrap the component with MobX injection and observation
const Signin = inject("data")(observer(({ data }) => {
    // === STATE HOOKS ===
    const [email, setEmail] = useState("");            // User's email input
    const [password, setPassword] = useState("");      // User's password input
    const [showPassword, setShowPassword] = useState(false);  // Password visibility toggle
    const [errorMessage, setErrorMessage] = useState(null);   // Error display for login issues

    // Regex pattern to validate email input
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // React Router hook for programmatic navigation after login
    const navigate = useNavigate();

    // === LANGUAGE SWITCHING FUNCTION ===
    const updateLang = () => {
        data.t.switchLang();        // Toggle language (French/English)
        data.t = new Translator();  // Reload translator instance with new language
    };

    // === FORM SUBMISSION HANDLER ===
    const handleSubmit = (event) => {
        event.preventDefault(); // Prevent default page reload

        // Prepare form data for API submission
        const formData = new FormData();
        formData.append("email", email.trim());
        formData.append("password", password);

        // POST request to /auth endpoint
        fetch(
            "/auth",
            "POST",
            formData,
            (response) => {
                // On success: store tokens, redirect, etc.
                AuthUtils.handleAuthSuccess(response, data, navigate, setErrorMessage);
            },
            () => {
                // On failure: show localized error message
                setErrorMessage(data.t.read("signin.invalid_credentials_provided"));
            }
        );
    };

    // === COMPONENT RENDER ===
    return (
        <div>
            {/* Language switcher and PWA install button */}
            <div
                className="pointer position-absolute"
                style={{ right: 20, top: 20, display: 'flex', alignItems: 'center', gap: 16 }}
            >
                <PwaInstallButton t={data.t} title={data.t.read('download_app')} />
                <span onClick={updateLang} style={{ cursor: 'pointer' }}>
                    {
                        data.t.lang === "fr" ?
                            <span><strong>Français</strong> / English</span> :
                            <span>Français / <strong>English</strong></span>
                    }
                </span>
            </div>

            {/* Centered login form wrapper */}
            <div className="page-centered">
                <div className="col-lg-3 col-md-4 col-sm-6">
                    
                    {/* Logo */}
                    <div className={`text-center ${data.isMobile ? 'mb-6' : 'mb-5'}`}>
                        <img src={logo} alt="Head Logo" />
                    </div>

                    {/* Error message alert box */}
                    <div className={`alert-section ${data.isMobile ? 'mb-4' : ''}`}>
                        {errorMessage && (
                            <div className="alert alert-danger" role="alert">
                                {errorMessage}
                            </div>
                        )}
                    </div>

                    {/* === SIGN-IN FORM === */}
                    <form onSubmit={handleSubmit}>

                        {/* Email field */}
                        <div className={`form-group ${data.isMobile ? 'mb-4' : ''}`}>
                            <input
                                className="form-control"
                                placeholder="Email"
                                required
                                type="email"
                                name="email"
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="email-input"
                            />
                        </div>

                        {/* Password field with eye icon toggle */}
                        <div className={`form-group position-relative ${data.isMobile ? 'mb-4' : ''}`}>
                            <input
                                className="form-control pr-5" // right padding for icon space
                                placeholder={data.t.read("password")}
                                required
                                type={showPassword ? "text" : "password"} // Toggle between password and text
                                name="password"
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="password-input"
                            />

                            {/* Eye / Eye-slash icon to toggle password visibility */}
                            <span
                                onClick={() => setShowPassword(prev => !prev)}
                                style={{
                                    position: 'absolute',
                                    right: 12,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    color: '#6c757d',
                                    padding: '8px', // touch area for mobile
                                }}
                                data-testid="toggle-password-visibility"
                                aria-label="Toggle password visibility"
                            >
                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </span>
                        </div>

                        {/* Sign-in button, only enabled if email is valid */}
                        <div className="form-group">
                            <button
                                disabled={!emailPattern.test(email)}
                                className={`btn btn-block btn-primary ${data.isMobile ? 'py-2' : ''}`}
                                data-testid="signin-button"
                            >
                                {data.t.read("signin.signin")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}));

// Export the component wrapped in MobX injector and observer
export default Signin;